/**
 * Selects Liquid-rendered price states. All money calculations stay in Liquid.
 * Only loaded when automatic-discount display is enabled in theme settings.
 */
(() => {
  const priceSelector = "[data-auto-discount-price]";
  const planControlSelector =
    '[name="selling_plan"], [data-selling-plan-id], [data-radio-type]';
  let timer;
  let queued = false;

  /** @param {Element} price */
  function selectedPlan(price) {
    // A recommendation/card for this same product must not inherit the PDP plan.
    const scope =
      price.closest("product-card, dialog, .shopify-section") ||
      price.parentElement;
    if (!scope) return price.getAttribute("data-selected-selling-plan") || "";

    const productId = price.getAttribute("data-product-id");
    const controls = Array.from(
      scope.querySelectorAll(planControlSelector),
    ).filter((control) => {
      if (control.disabled) return false;
      if (control.closest("product-card, dialog, .shopify-section") !== scope)
        return false;
      if (control.closest(".shopify_subscriptions_app_block--hidden"))
        return false;
      const owner = control.closest(
        "product-form-component[data-product-id], .shopify_subscriptions_app_container[data-product-id]",
      );
      return !owner || owner.getAttribute("data-product-id") === productId;
    });

    if (
      controls.some(
        (control) =>
          control.getAttribute("data-radio-type") === "one_time_purchase" &&
          control.checked,
      )
    ) {
      return "";
    }

    // Read the successful form field first: that is the actual plan sent to cart.
    const formControls = controls.filter(
      (control) =>
        control.getAttribute("name") === "selling_plan" && !control.disabled,
    );
    for (const control of formControls) {
      if (
        (control.type === "radio" || control.type === "checkbox") &&
        !control.checked
      )
        continue;
      if (control.value) return control.value;
    }

    const selectedControl = controls.find(
      (control) =>
        control.checked &&
        (control.getAttribute("data-selling-plan-id") ||
          control.getAttribute("data-radio-type") === "selling_plan"),
    );
    if (selectedControl)
      return (
        selectedControl.getAttribute("data-selling-plan-id") ||
        selectedControl.value ||
        "unknown-plan"
      );

    // An empty live form field explicitly selects a one-time purchase.
    if (formControls.length) return "";
    return price.getAttribute("data-selected-selling-plan") || "";
  }

  /** @param {Element | null} element @param {boolean} visible */
  function show(element, visible) {
    if (!element) return;
    element.toggleAttribute("hidden", !visible);
  }

  /** @param {Element} price @param {number} now */
  function updatePrice(price, now) {
    const sale = price.querySelector("[data-auto-discount-sale]");
    const regular = price.querySelector("[data-auto-discount-regular]");
    const planPrice = price.querySelector("[data-auto-discount-plan-price]");
    const planId = selectedPlan(price);
    const planTemplate = planId
      ? Array.from(
          price.querySelectorAll("template[data-auto-discount-plan]"),
        ).find(
          (template) =>
            template.getAttribute("data-auto-discount-plan") === planId,
        )
      : null;
    const hasPlanPrice = Boolean(planTemplate && planPrice);

    if (
      hasPlanPrice &&
      (planPrice.getAttribute("data-rendered-plan") !== planId ||
        !planPrice.hasChildNodes())
    ) {
      planPrice.replaceChildren(planTemplate.content.cloneNode(true));
      planPrice.setAttribute("data-rendered-plan", planId);
    }

    const ends = Number(price.getAttribute("data-auto-discount-ends"));
    const expired = ends > 0 && now >= ends;
    const showSale = Boolean(sale && !planId && !expired);
    show(sale, showSale);
    show(regular, !showSale && !hasPlanPrice);
    show(planPrice, hasPlanPrice);
    return ends > now ? ends : 0;
  }

  function refresh() {
    queued = false;
    clearTimeout(timer);
    const now = Date.now() / 1000;
    let nextEnds = 0;
    let hasPlans = false;

    for (const price of document.querySelectorAll(priceSelector)) {
      const ends = updatePrice(price, now);
      if (ends && (!nextEnds || ends < nextEnds)) nextEnds = ends;
      hasPlans ||= Boolean(
        price.querySelector("template[data-auto-discount-plan]"),
      );
    }

    // Some subscription apps assign hidden input.value without dispatching change.
    // Poll only pages containing selling plans; ordinary grids use one expiry timer.
    const delay = Math.min(
      hasPlans ? 1000 : Infinity,
      nextEnds ? Math.max(1, (nextEnds - now) * 1000) : Infinity,
      2147483647,
    );
    if (hasPlans || nextEnds) timer = setTimeout(refresh, delay);
  }

  function scheduleRefresh() {
    if (queued) return;
    queued = true;
    queueMicrotask(refresh);
  }

  function init() {
    refresh();
    new MutationObserver(scheduleRefresh).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      // Exclude the hidden attributes and plan holder changed by this guard.
      attributeFilter: [
        "data-auto-discount-ends",
        "data-selected-selling-plan",
        "data-variant-id",
        "value",
        "checked",
        "disabled",
      ],
    });
    document.addEventListener("change", scheduleRefresh);
    document.addEventListener("input", scheduleRefresh);
    document.addEventListener("variant:update", scheduleRefresh, true);
    document.addEventListener("shopify:section:load", scheduleRefresh);
    document.addEventListener("visibilitychange", scheduleRefresh);
    window.addEventListener("pageshow", scheduleRefresh);
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
